import type { Currency, OperationType, Property, PropertyFilters, PropertyStatus } from '@/types';

export function formatPrice(
  amount: number,
  currency: Currency,
  options?: { negotiable?: boolean; showPrice?: boolean; priceLabel?: string },
): string {
  if (options?.showPrice === false) return 'Consultar precio';
  if (options?.priceLabel) return options.priceLabel;

  const formatted = new Intl.NumberFormat('es-VE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return options?.negotiable ? `${formatted} (Negociable)` : formatted;
}

export function getOperationLabel(operation: OperationType): string {
  const labels: Record<OperationType, string> = {
    sale: 'Venta',
    rent: 'Alquiler',
    'short-term-rent': 'Alquiler temporal',
  };
  return labels[operation];
}

export function getStatusLabel(status: PropertyStatus): string {
  const labels: Record<PropertyStatus, string> = {
    draft: 'Borrador',
    available: 'Disponible',
    reserved: 'Reservado',
    sold: 'Vendido',
    rented: 'Alquilado',
    withdrawn: 'Retirado',
  };
  return labels[status];
}

export function getStatusColor(status: PropertyStatus): string {
  const colors: Record<PropertyStatus, string> = {
    draft: 'bg-gray-200 text-gray-700',
    available: 'bg-green-100 text-green-800',
    reserved: 'bg-yellow-100 text-yellow-800',
    sold: 'bg-gray-200 text-gray-600',
    rented: 'bg-blue-100 text-blue-800',
    withdrawn: 'bg-gray-100 text-gray-500',
  };
  return colors[status];
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function buildWhatsAppUrl(phone: string, message: string): string {
  const cleanPhone = phone.replace(/\D/g, '');
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
}

export function buildPropertyWhatsAppMessage(code: string, title: string, url: string): string {
  return `Hola, deseo recibir información sobre la propiedad ${code}: ${title}. Enlace: ${url}`;
}

export function buildGeneralWhatsAppMessage(): string {
  return 'Hola, deseo recibir información sobre los servicios de UGA Bienes Raíces.';
}

export function buildOwnerWhatsAppMessage(): string {
  return 'Hola, deseo ofrecer una propiedad para venta o alquiler.';
}

export function buildLegalWhatsAppMessage(): string {
  return 'Hola, deseo solicitar información sobre asesoría jurídica inmobiliaria.';
}

export function getPublicProperties(properties: Property[]): Property[] {
  return properties.filter((p) => p.status !== 'draft' && p.status !== 'withdrawn');
}

export function filterProperties(properties: Property[], filters: PropertyFilters): Property[] {
  let result = getPublicProperties(properties);

  if (filters.operation) {
    result = result.filter((p) => p.operations.includes(filters.operation!));
  }
  if (filters.propertyType) {
    result = result.filter((p) => p.propertyType === filters.propertyType);
  }
  if (filters.state) {
    result = result.filter((p) => p.location.state === filters.state);
  }
  if (filters.city) {
    result = result.filter((p) => p.location.city === filters.city);
  }
  if (filters.municipality) {
    result = result.filter((p) => p.location.municipality === filters.municipality);
  }
  if (filters.sector) {
    const sector = filters.sector.toLowerCase();
    result = result.filter((p) => p.location.sector?.toLowerCase().includes(sector));
  }
  if (filters.priceMin !== undefined) {
    result = result.filter((p) => p.price.amount >= filters.priceMin!);
  }
  if (filters.priceMax !== undefined) {
    result = result.filter((p) => p.price.amount <= filters.priceMax!);
  }
  if (filters.currency) {
    result = result.filter((p) => p.price.currency === filters.currency);
  }
  if (filters.bedrooms !== undefined) {
    result = result.filter((p) => (p.bedrooms ?? 0) >= filters.bedrooms!);
  }
  if (filters.bathrooms !== undefined) {
    result = result.filter((p) => (p.bathrooms ?? 0) >= filters.bathrooms!);
  }
  if (filters.parkingSpaces !== undefined) {
    result = result.filter((p) => (p.parkingSpaces ?? 0) >= filters.parkingSpaces!);
  }
  if (filters.areaMin !== undefined) {
    result = result.filter((p) => (p.areas.constructionM2 ?? p.areas.landM2 ?? 0) >= filters.areaMin!);
  }
  if (filters.areaMax !== undefined) {
    result = result.filter((p) => (p.areas.constructionM2 ?? p.areas.landM2 ?? Infinity) <= filters.areaMax!);
  }
  if (filters.furnished !== undefined) {
    result = result.filter((p) => p.furnished === filters.furnished);
  }
  if (filters.petsAllowed !== undefined) {
    result = result.filter((p) => p.petsAllowed === filters.petsAllowed);
  }
  if (filters.featured !== undefined) {
    result = result.filter((p) => p.featured === filters.featured);
  }
  if (filters.code) {
    result = result.filter((p) => p.code.toLowerCase() === filters.code!.toLowerCase());
  }
  if (filters.keyword) {
    const kw = filters.keyword.toLowerCase();
    result = result.filter(
      (p) =>
        p.title.toLowerCase().includes(kw) ||
        p.shortDescription.toLowerCase().includes(kw) ||
        p.location.sector?.toLowerCase().includes(kw) ||
        p.location.city.toLowerCase().includes(kw),
    );
  }

  return sortProperties(result, filters.sort ?? 'newest');
}

export function sortProperties(properties: Property[], sort: NonNullable<PropertyFilters['sort']>): Property[] {
  const sorted = [...properties];
  switch (sort) {
    case 'price-asc':
      return sorted.sort((a, b) => a.price.amount - b.price.amount);
    case 'price-desc':
      return sorted.sort((a, b) => b.price.amount - a.price.amount);
    case 'area-asc':
      return sorted.sort(
        (a, b) =>
          (a.areas.constructionM2 ?? a.areas.landM2 ?? 0) -
          (b.areas.constructionM2 ?? b.areas.landM2 ?? 0),
      );
    case 'area-desc':
      return sorted.sort(
        (a, b) =>
          (b.areas.constructionM2 ?? b.areas.landM2 ?? 0) -
          (a.areas.constructionM2 ?? a.areas.landM2 ?? 0),
      );
    case 'newest':
    default:
      return sorted.sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
  }
}

export function paginateProperties<T>(items: T[], page: number, limit: number): { items: T[]; total: number; totalPages: number } {
  const total = items.length;
  const totalPages = Math.ceil(total / limit);
  const start = (page - 1) * limit;
  return { items: items.slice(start, start + limit), total, totalPages };
}

export function getSimilarProperties(property: Property, allProperties: Property[], limit = 4): Property[] {
  return getPublicProperties(allProperties)
    .filter(
      (p) =>
        p.id !== property.id &&
        p.status === 'available' &&
        (p.propertyType === property.propertyType ||
          p.location.city === property.location.city ||
          p.operations.some((op) => property.operations.includes(op))),
    )
    .slice(0, limit);
}

export function getLocationDisplay(property: Property): string {
  const parts = [property.location.sector, property.location.city, property.location.state].filter(Boolean);
  return parts.join(', ');
}

export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}
